import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppContentMaxWidth from '@lib/layouts/AppLayout/AppContentMaxWidth/AppContentMaxWidth';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import formStyles from '@lib/assets/styles/Form.module.scss';
import FieldNames from '@lib/enums/fieldNames/interventionActionFieldNames';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import TopNavigationHeader from '@lib/layouts/AppLayout/TopNavigationHeader/TopNavigationHeader';
import { APP_URLS } from 'constants/urls';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import getInterventionActionFormFields from '../utils/getInterventionActionFormFields';
import getInterventionActionDefaultValuesFromQueryData, {
  DEFAULT_VALUES,
} from '../utils/getInterventionActionDefaultValuesFromQueryData';
import { UseInterventionActionActionsResult } from '../utils/useInterventionActionActions';
import useSearchListQueries from '../utils/useSearchListQueries';
import { InterventionViewMode } from '../enums';

type Props = {
  useInterventionActionActionsResult: UseInterventionActionActionsResult;
  viewMode: InterventionViewMode;
};

export default function InterventionActionFormPage({
  useInterventionActionActionsResult,
  viewMode,
}: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const navigate = useNavigate();
  const useSearchListQueriesResult = useSearchListQueries();
  const {
    actionId,
    interventionActionQueryHookResult,
    interventionId,
    interventionQueryHookResult,
    loading,
    mutationError,
    onSubmit,
    usersQueryHookResult,
    fetchUsersSearchListLazyQuery,
  } = useInterventionActionActionsResult;
  const { allIds: assigneeIds } = usersQueryHookResult;

  const isCreatePage = viewMode === InterventionViewMode.AddAction;

  const siteId = interventionQueryHookResult.data?.data?.siteId || '';

  const defaultValues = isCreatePage
    ? DEFAULT_VALUES
    : getInterventionActionDefaultValuesFromQueryData(
        interventionActionQueryHookResult.data,
        assigneeIds,
      );
  const formDefaultValues = {
    ...defaultValues,
    [FieldNames.Site]: siteId ? { value: siteId, label: siteId } : null,
  };

  const fields = getInterventionActionFormFields({
    assigneeIds,
    actionId,
    t,
    getAssigneeLoadOptions: useSearchListQueriesResult.getAssigneeLoadOptions,
    fetchUsersSearchListLazyQuery,
  });

  const actionsComponent = () => (
    <ActionsRow
      className={formStyles.formActions}
      actions={[
        <Button
          onClick={() => navigate(-1)}
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          fullWidth
          data-test-id="intervention-action-form__cancel-button"
        />,
        <Button
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t(isCreatePage ? 'create' : 'save-changes')}
          leftIcon="check"
          type="submit"
          fullWidth
          disabled={loading}
          data-test-id="intervention-action-form__save-button"
        />,
      ]}
    />
  );

  return (
    <>
      <TopNavigationHeader
        title={t(isCreatePage ? 'create-an-action' : 'edit-an-action')}
        backUrl={APP_URLS.app.interventions.viewActions.getDynamicPath({
          pathParts: { id: interventionId },
        })}
      />
      <PageBody withoutHeader>
        <AppContentMaxWidth>
          {interventionQueryHookResult?.loading ? (
            <CircleLoader />
          ) : (
            <Form
              actionsComponent={actionsComponent}
              actionsComponentProps={{ isCreatePage }}
              defaultValues={formDefaultValues}
              fields={fields}
              formId="intervention-action"
              formRef={formRef}
              graphQLErrors={getGraphQLErrors(mutationError)}
              loading={loading}
              onSubmit={onSubmit}
            />
          )}
        </AppContentMaxWidth>
      </PageBody>
    </>
  );
}
